/**
 * OfflineEventsDrawer Component - Detailed offline events analysis drawer
 * 
 * Features:
 * - Detailed offline events data display with timestamps
 * - Sortable table columns for event analysis
 * - Excel export functionality for offline events
 * - Date range filtering integration
 * - Pagination for large event datasets
 * - Real-time data refresh capabilities
 * - Error handling and loading states
 * 
 * @author CTS - Ford PM UI Team
 
 */

import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { getOfflineEvents } from '../../api/outageApi';
import {
  OfflineEventsDrawer as OfflineEventsDrawerContainer,
  OfflineEventsDrawerHeader,
  OfflineEventsDrawerTitle,
  OfflineEventsDrawerDeviceName,
  OfflineEventsDrawerContent,
  OfflineEventsMetaSection,
  DeviceDetailRowContainer,
  OfflineEventsTableWrapper,
  OfflineEventsLoadingMessage,
  OfflineEventsSpinner,
  OfflineEventsErrorBox,
  OfflineEventsErrorMessage,
  OfflineEventsPagination,
  OfflineEventsEmptyState,
  OutagePrimaryButton,
  OutageSecondaryButton,
  OutageEventsTableTitle
} from './OfflineEventsDrawer.styles';
import { OutagePanel } from './Outage.styles';
import StatusIndicator from '../../components/StatusIndicator.jsx';
import { Typography, Box } from '@mui/material';
import {
  Close as CloseIcon,
  ArrowUpward,
  ArrowDownward,
  UnfoldMore,
  Download as DownloadIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import DateUtils from '../../utils/DateUtils.jsx';
import { AppLabels } from '../../constant/constant';
import { FORMAT_TYPES, formatOutageDuration } from '../../utils/durationUtils.jsx';
import { renderCriticalDeviceList, formattedText } from '../../utils/TextTooltipUtils.jsx';
import { DashboardMonospaceText } from '../dashBoard/Dashboard.styles';

// Constants to avoid magic numbers
const PAGINATION_CONFIG = {
  PAGE_SIZE: 20,
  INITIAL_PAGE: 1
};

const STYLE_CONFIG = {
  ICON_SIZE: '16px',
  GAP_SIZE: '4px',
  FONT_SIZE_SMALL: '0.95rem',
  MARGIN_SMALL: '8px',
  MARGIN_MEDIUM: '16px',
  MAX_VISIBLE_PAGES: 5,
  PAGE_OFFSET: 2
};



// Helper function to render device detail row
const DeviceDetailRow = ({ label, value, isStatus = false }) => (
  <DeviceDetailRowContainer>
    <Typography variant="caption" className="detail-label">
      {label}
    </Typography>
    {isStatus ? (
      <StatusIndicator status={value || AppLabels.notApplicable} />
    ) : (
      <Typography variant="body2" className="detail-value">
        {value || AppLabels.notApplicable}
      </Typography>
    )}
  </DeviceDetailRowContainer>
);

// PropTypes validation for SortableHeader component
DeviceDetailRow.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  isStatus: PropTypes.bool,
};

// Sortable Header Component
const SortableHeader = ({ children, field, sortField, sortDirection, onSort }) => {
  const handleSort = () => {
    if (sortField === field) {
      // Toggle direction if same field
      onSort(field, sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with ascending direction
      onSort(field, 'asc');
    }
  };

  const getSortIcon = () => {
    if (sortField !== field) {
      return <UnfoldMore sx={{ fontSize: STYLE_CONFIG.ICON_SIZE, opacity: 0.5 }} />;
    }
    return sortDirection === 'asc'
      ? <ArrowUpward sx={{ fontSize: STYLE_CONFIG.ICON_SIZE }} />
      : <ArrowDownward sx={{ fontSize: STYLE_CONFIG.ICON_SIZE }} />;
  };

  return (
    <th
      onClick={handleSort}
      style={{
        cursor: 'pointer',
        userSelect: 'none',
        position: 'relative'
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: STYLE_CONFIG.GAP_SIZE
      }}>
        <span>{children}</span>
        {getSortIcon()}
      </div>
    </th>
  );
};

// PropTypes validation for SortableHeader component
SortableHeader.propTypes = {
  children: PropTypes.node.isRequired,
  field: PropTypes.string.isRequired,
  sortField: PropTypes.string,
  sortDirection: PropTypes.oneOf(['asc', 'desc']).isRequired,
  onSort: PropTypes.func.isRequired
};

// Default props for SortableHeader component
SortableHeader.defaultProps = {
  sortField: null
};

const OfflineEventsDrawer = ({
  open,
  onClose,
  plugId,
  deviceDetails,
  startDate,
  endDate
}) => {
  // Data state
  const [offlineEventsData, setOfflineEventsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(PAGINATION_CONFIG.INITIAL_PAGE);
  const pageSize = PAGINATION_CONFIG.PAGE_SIZE;

  // Sorting state
  const [sortField, setSortField] = useState('offline_Date_Time');
  const [sortDirection, setSortDirection] = useState('desc');

  // Helper function to get offline events count
  const getOfflineEventsCount = () => {
    if (offlineEventsData?.length > 0) {
      return offlineEventsData.length;
    }
    if (deviceDetails?.offlineEvents) {
      return deviceDetails.offlineEvents;
    }
    return 0;
  };

  // Helper function to render error message with appropriate styling
  const renderErrorMessage = () => {
    const isNoDataError = error.includes('No offline events found') || error.includes('No events found');

    return (
      <Typography sx={{
        color: isNoDataError ? '#6b7280' : '#ef4444',
        fontSize: STYLE_CONFIG.FONT_SIZE_SMALL
      }}>
        {isNoDataError ? error : `Error loading offline events: ${error}`}
      </Typography>
    );
  };

  // Helper function to render main content based on state
  const renderMainContent = () => {
    if (loading) {
      return (
        <OfflineEventsLoadingMessage>
          <OfflineEventsSpinner />
          <Typography>Loading offline events...</Typography>
        </OfflineEventsLoadingMessage>
      );
    }

    if (error) {
      return (
        <OfflineEventsErrorBox>
          <OfflineEventsErrorMessage>
            {renderErrorMessage()}
          </OfflineEventsErrorMessage>
        </OfflineEventsErrorBox>
      );
    }

    if (!offlineEventsData || offlineEventsData.length === 0) {
      return (
        <OfflineEventsEmptyState>
          <Typography variant="h6" sx={{ marginBottom: STYLE_CONFIG.MARGIN_SMALL }}>
            {AppLabels.offlineEventsLabels.noOfflineEventsFound}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280' }}>
            {AppLabels.offlineEventsLabels.noOfflineEventsDescription}
          </Typography>
        </OfflineEventsEmptyState>
      );
    }

    // Render the data table
    return (
      <OutagePanel>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px'
        }}>
          <OutageEventsTableTitle>
            {AppLabels.offlineEventsLabels.offlineEventsDetails} ({offlineEventsData?.length || 0} {AppLabels.offlineEventsLabels.eventsCount})
          </OutageEventsTableTitle>

          {offlineEventsData && offlineEventsData.length > 0 && (
            <OutagePrimaryButton
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
            >
              {AppLabels.offlineEventsLabels.downloadExcel}
            </OutagePrimaryButton>
          )}
        </Box>

        <OfflineEventsTableWrapper>
          <table>
            <thead>
              <tr>
                <SortableHeader
                  field="offline_Date_Time"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                >
                  {AppLabels.offlineEventsLabels.offlineTimeHeader}
                </SortableHeader>
                <SortableHeader
                  field="online_Date_Time"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                >
                  {AppLabels.offlineEventsLabels.onlineTimeHeader}
                </SortableHeader>
                <SortableHeader
                  field="status"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                >
                  {AppLabels.offlineEventsLabels.statusHeader}
                </SortableHeader>
                <SortableHeader
                  field="outage_Duration_In_Minutes"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                >
                 {AppLabels.offlineEventsLabels.durationHeader}
                </SortableHeader>
              </tr>
            </thead>
            <tbody>
              {getPaginatedData().map((event, index) => (
                <tr key={event.offline_time || event.online_time || `event-${index}`}>
                  <td>{DateUtils.formatDateWithLocalZone(event.offline_time) || AppLabels.notApplicable}</td>
                  <td>{DateUtils.formatDateWithLocalZone(event.online_time) || AppLabels.notApplicable}</td>
                  <td>
                    <StatusIndicator status={event.status || AppLabels.notApplicable} />
                  </td>
                  <td>{event.outage_Duration_In_Minutes === null ? AppLabels.notApplicable : formatOutageDuration(event.outage_Duration_In_Minutes.toString(), FORMAT_TYPES.VERBOSE)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </OfflineEventsTableWrapper>

        {/* Pagination */}
        {totalPages > PAGINATION_CONFIG.INITIAL_PAGE && (
          <OfflineEventsPagination>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: STYLE_CONFIG.MARGIN_SMALL }}>
              <button
                onClick={() => setPage(p => Math.max(PAGINATION_CONFIG.INITIAL_PAGE, p - 1))}
                disabled={page === PAGINATION_CONFIG.INITIAL_PAGE}
              >
                {AppLabels.offlineEventsLabels.previousButton}
              </button>

              {getPageNumbers().map(pageNum => (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={page === pageNum ? 'active' : ''}
                >
                  {pageNum}
                </button>
              ))}

              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                {AppLabels.offlineEventsLabels.nextButton}
              </button>
            </Box>
          </OfflineEventsPagination>
        )}
      </OutagePanel>
    );
  };

  // Ref to prevent duplicate API calls
  const apiCalled = useRef(false);

  // Fetch offline events data
  const fetchOfflineEventsData = async (forceRefresh = false) => {
    if (!forceRefresh && apiCalled.current) {
      return;
    }

    if (!plugId) return;

    try {
      setLoading(true);
      setError(null);
      apiCalled.current = true;

      // Prepare date parameters
      const dateParams = {};
      if (startDate) {
        dateParams.start_date = startDate;
      }
      if (endDate) {
        dateParams.end_date = endDate;
      }

      const response = await getOfflineEvents(plugId, dateParams);
      setOfflineEventsData(response.outage_Events || []);
    } catch (err) {
      setError(err.message || AppLabels.offlineEventsLabels.failedToLoadEvents);
      apiCalled.current = false; // Reset on error to allow retry
    } finally {
      setLoading(false);
    }
  };

  // Reset API call flag when plugId changes
  useEffect(() => {
    if (plugId) {
      apiCalled.current = false;
      setPage(PAGINATION_CONFIG.INITIAL_PAGE); // Reset pagination
      fetchOfflineEventsData();
    }
  }, [plugId]);

  // Handle Excel download
  const handleDownload = () => {
    if (!offlineEventsData || offlineEventsData.length === 0) return;

    // Prepare data for Excel export
    const exportData = offlineEventsData.map((event, index) => ({
      [AppLabels.offlineEventsLabels.excelSerialNumber]: index + 1,
      [AppLabels.offlineEventsLabels.excelOfflineTime]: DateUtils.formatDateWithLocalZone(event.offline_time) || AppLabels.notApplicable,
      [AppLabels.offlineEventsLabels.excelOnlineTime]: DateUtils.formatDateWithLocalZone(event.online_time) || AppLabels.notApplicable,
      [AppLabels.offlineEventsLabels.excelStatus]: event.status || AppLabels.notApplicable,
      [AppLabels.offlineEventsLabels.excelDuration]: event.outage_Duration_In_Minutes === null ? AppLabels.notApplicable : formatOutageDuration(event.outage_Duration_In_Minutes.toString(), FORMAT_TYPES.VERBOSE)
    }));

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, AppLabels.offlineEventsLabels.excelSheetName);

    // Generate filename with device ID and current date
    const currentDate = new Date().toISOString().split('T')[0];
    const filename = `${AppLabels.offlineEventsLabels.excelFilenamePrefix}${deviceDetails.deviceName}_${deviceDetails.site}_${deviceDetails.location}_${deviceDetails.deviceMac}_${currentDate}.xlsx`;

    // Download the file
    XLSX.writeFile(workbook, filename);
  };


  // Handle sorting
  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setPage(PAGINATION_CONFIG.INITIAL_PAGE); // Reset to first page when sorting
  };

  // Sort and paginate data
  const getSortedData = () => {
    if (!offlineEventsData || offlineEventsData.length === 0) return [];

    const sorted = [...offlineEventsData].sort((a, b) => {
      const aVal = a[sortField] || '';
      const bVal = b[sortField] || '';

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return sorted;
  };

  const getPaginatedData = () => {
    const sorted = getSortedData();
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sorted.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil((offlineEventsData?.length || 0) / pageSize);


  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = STYLE_CONFIG.MAX_VISIBLE_PAGES;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const start = Math.max(PAGINATION_CONFIG.INITIAL_PAGE, page - STYLE_CONFIG.PAGE_OFFSET);
      const end = Math.min(totalPages, start + maxVisiblePages - 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  return (
    <OfflineEventsDrawerContainer
      anchor="right"
      open={open}
      onClose={onClose}
      disableScrollLock={true}
    >
      {/* Header */}
      <OfflineEventsDrawerHeader>
        <Box>
          <OfflineEventsDrawerTitle variant="caption">
            {AppLabels.offlineEventsLabels.drawerTitle}
          </OfflineEventsDrawerTitle>
          <OfflineEventsDrawerDeviceName variant="h5" component="h3">
            {deviceDetails?.deviceName || AppLabels.notApplicable}
          </OfflineEventsDrawerDeviceName>
        </Box>

        <OutageSecondaryButton onClick={onClose}>
          <CloseIcon sx={{ marginRight: '8px', fontSize: '18px' }} />
          {AppLabels.offlineEventsLabels.closeDrawer}
        </OutageSecondaryButton>
      </OfflineEventsDrawerHeader>

      {/* Content */}
      <OfflineEventsDrawerContent>
        {/* Device Meta Information */}
        {deviceDetails && (
          <OfflineEventsMetaSection>
            <DeviceDetailRow
              label={AppLabels.offlineEventsLabels.plugIdLabel}
              value={plugId}
            />
            <DeviceDetailRow
              label={AppLabels.offlineEventsLabels.deviceNameLabel}
              value={deviceDetails.deviceName || AppLabels.notApplicable}
            />
            <DeviceDetailRow
              label={AppLabels.offlineEventsLabels.siteLabel}
              value={deviceDetails.site || AppLabels.notApplicable}
            />
            <DeviceDetailRow
              label={AppLabels.offlineEventsLabels.locationLabel}
              value={deviceDetails.location || AppLabels.notApplicable}
            />
            <DeviceDetailRow
              label={AppLabels.offlineEventsLabels.deviceMacLabel}
              value={<DashboardMonospaceText>{deviceDetails.deviceMac}</DashboardMonospaceText>}
            />
            <DeviceDetailRow
              label={AppLabels.offlineEventsLabels.statusLabel}
              value={deviceDetails.statusNow}
              isStatus={true}
            />
            <DeviceDetailRow
              label={AppLabels.offlineEventsLabels.modemIdLabel}
              value={<DashboardMonospaceText>{deviceDetails.modemId}</DashboardMonospaceText>}
            />
             <DeviceDetailRow
              label={AppLabels.offlineEventsLabels.smartPlugLocationLabel}
              value={formattedText(deviceDetails.smartPlugLocation)}
            />
            <DeviceDetailRow
              label={AppLabels.offlineEventsLabels.singleSourceDevicesLabel}
              value={renderCriticalDeviceList(deviceDetails.criticalDevices, true)}
            />
            <DeviceDetailRow
              label={AppLabels.offlineEventsLabels.dualSourceDevicesLabel}
              value={renderCriticalDeviceList(deviceDetails.dualSupplyDevices, true)}
            />
            <DeviceDetailRow
              label={AppLabels.offlineEventsLabels.offlineEventsLabel}
              value={getOfflineEventsCount()}
            />
             <DeviceDetailRow
              label={AppLabels.offlineEventsLabels.outageLabel}
              value={<DashboardMonospaceText>{deviceDetails.outageDisplay}</DashboardMonospaceText>}
            />
          </OfflineEventsMetaSection>
        )}

        {/* Content Area */}
        {renderMainContent()}
      </OfflineEventsDrawerContent>
    </OfflineEventsDrawerContainer>
  );
};

// PropTypes validation for OfflineEventsDrawer component
OfflineEventsDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  plugId: PropTypes.string.isRequired,
  deviceDetails: PropTypes.shape({
    id: PropTypes.string,
    site: PropTypes.string,
    siteId: PropTypes.string,
    location: PropTypes.string,
    locationId: PropTypes.string,
    deviceMac: PropTypes.string,
    status: PropTypes.string,
    model: PropTypes.string,
    statusNow: PropTypes.string,
    isModem: PropTypes.bool,
    modemId: PropTypes.string,
    smartPlugLocation: PropTypes.string,
    criticalDeviceList: PropTypes.string,
    criticalDevices: PropTypes.array,
    dualSupplyDevices: PropTypes.array,
    outageDisplay: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    offlineEvents: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    deviceName: PropTypes.string,
  }),
  startDate: PropTypes.string,
  endDate: PropTypes.string
};

// Default props for OfflineEventsDrawer component
OfflineEventsDrawer.defaultProps = {
  deviceDetails: {
    id: '',
    site: '',
    siteId: '',
    location: '',
    locationId: '',
    deviceMac: '',
    status: '',
    model: '',
    statusNow: '',
    isModem: false,
    modemId: '',
    outageDisplay: '',
    offlineEvents: 0,
    smartPlugLocation: '',
    deviceName: '',
    criticalDeviceList: '',
    criticalDevices: [],
    dualSupplyDevices: []
  },
  startDate: '',
  endDate: ''
};

export default OfflineEventsDrawer;
